import { type ReactNode } from 'react';
import { Box, Divider, Flex, Loader, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconCheck, IconRefresh } from '@tabler/icons-react';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { ReviewStatusIndicator } from 'src/design-system/ReviewStatusIndicator';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { splitMarkdownSections } from './splitMarkdownSections';

type Props = {
  title: string;
  markdownContent: string;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  isReviewed?: boolean;
  onToggleReviewed?: (isReviewed: boolean) => void;
  isSettingReviewed?: boolean;
};

export function ReviewableMeetingNote({
  title,
  markdownContent,
  onRegenerate,
  isRegenerating = false,
  isReviewed = false,
  onToggleReviewed,
  isSettingReviewed = false,
}: Props) {
  const sections = splitMarkdownSections(markdownContent);

  return (
    <Box>
      <Flex justify="space-between" align="center" gap="sm">
        <Flex align="center" gap="xs" style={{ minWidth: 0 }}>
          <ReviewStatusIndicator isReviewed={isReviewed} />
          <Text size="lg" fw={FONT_WEIGHTS.semibold} c="gray.10" truncate style={{ minWidth: 0 }}>
            {title}
          </Text>
          <Text
            size="sm"
            fw={FONT_WEIGHTS.medium}
            c={isReviewed ? 'success.7' : 'gray.6'}
            style={{ flexShrink: 0 }}
          >
            {isReviewed ? 'Reviewed' : 'Not reviewed'}
          </Text>
        </Flex>
        <Flex align="center" gap="md" style={{ flexShrink: 0 }}>
          <HeaderAction
            icon={
              isRegenerating ? (
                <Loader size={16} color="var(--mantine-color-brand-6)" />
              ) : (
                <IconRefresh size={16} color="var(--mantine-color-brand-6)" />
              )
            }
            onClick={onRegenerate}
            disabled={!onRegenerate || isRegenerating || isReviewed}
          >
            Regenerate
          </HeaderAction>
          <HeaderAction
            icon={
              isSettingReviewed ? (
                <Loader size={16} color="var(--mantine-color-brand-6)" />
              ) : (
                <IconCheck size={16} color="var(--mantine-color-brand-6)" />
              )
            }
            onClick={onToggleReviewed ? () => onToggleReviewed(!isReviewed) : undefined}
            disabled={!onToggleReviewed || isSettingReviewed}
          >
            {isReviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
          </HeaderAction>
        </Flex>
      </Flex>

      <Divider mt="sm" color="gray.2" />

      <Stack mt="sm" gap="xxxs">
        {sections.map((section, index) => (
          <MarkdownEditor
            key={`${index}-${section.slice(0, 40)}`}
            defaultValue={section}
            readonly
            withBorder={false}
            bg="white"
            rootClass="compact"
            showMarkers={false}
            style={{ borderWidth: 0 }}
            px="xs"
            py={0}
          />
        ))}
      </Stack>
    </Box>
  );
}

function HeaderAction({
  icon,
  children,
  onClick,
  disabled = false,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}
    >
      <Flex align="center" gap="xxxs">
        {icon}
        <Text size="sm" fw={FONT_WEIGHTS.semibold} c="brand.6">
          {children}
        </Text>
      </Flex>
    </UnstyledButton>
  );
}
